import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorSafe } from '@/contexts/AdvisorContext';

/**
 * Per-firm feature overrides (B2B Phase A, #1648). An advisor toggles features
 * for their firm; firm-managed clients inherit their connected advisor's
 * overrides. Feeds the `firmOverride` slot in the feature resolver.
 */

/** The firm owner whose overrides apply: the advisor themselves, or (for a
 *  firm-managed client) their active advisor. */
export function useFirmOwnerId() {
  const { user } = useAuth();
  const { isAdvisorMode } = useAdvisorSafe();
  return useQuery<string | null>({
    queryKey: ['firm-owner-id', user?.id, isAdvisorMode],
    queryFn: async () => {
      if (!user?.id) return null;
      if (isAdvisorMode) return user.id;
      const { data } = await supabase
        .from('advisor_connections')
        .select('advisor_user_id')
        .eq('client_user_id', user.id)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      return (data as { advisor_user_id: string } | null)?.advisor_user_id ?? null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });
}

/** { feature_key: enabled } for the current firm. Empty when none/not a firm. */
export function useFirmFeatureOverrides(): Record<string, boolean> {
  const { data: ownerId } = useFirmOwnerId();
  const { data } = useQuery<Record<string, boolean>>({
    queryKey: ['firm-feature-overrides', ownerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('firm_feature_overrides')
        .select('feature_key, enabled')
        .eq('owner_user_id', ownerId!);
      if (error) throw error;
      const map: Record<string, boolean> = {};
      for (const r of (data ?? []) as { feature_key: string; enabled: boolean }[]) {
        map[r.feature_key] = r.enabled;
      }
      return map;
    },
    enabled: !!ownerId,
    staleTime: 1000 * 60,
  });
  return data ?? {};
}

/** Set (true/false) or clear (null → revert to default) a firm override. */
export function useSetFeatureOverride() {
  const { user } = useAuth();
  const { data: ownerId } = useFirmOwnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ featureKey, enabled }: { featureKey: string; enabled: boolean | null }) => {
      if (!ownerId) throw new Error('No firm owner to set overrides for');
      if (enabled === null) {
        const { error } = await supabase
          .from('firm_feature_overrides')
          .delete()
          .eq('owner_user_id', ownerId)
          .eq('feature_key', featureKey);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('firm_feature_overrides')
          .upsert(
            { owner_user_id: ownerId, feature_key: featureKey, enabled, updated_by: user?.id, updated_at: new Date().toISOString() },
            { onConflict: 'owner_user_id,feature_key' },
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firm-feature-overrides', ownerId] });
    },
  });
}
